import { useMemo } from 'react';
import {
  isSameDay as dfIsSameDay,
  isSameMonth as dfIsSameMonth,
  isSameYear as dfIsSameYear,
  startOfDay as dfStartOfDay,
  endOfDay as dfEndOfDay,
  addDays as dfAddDays,
  subDays as dfSubDays,
  startOfMonth as dfStartOfMonth,
  endOfMonth as dfEndOfMonth,
  addMonths as dfAddMonths,
  subMonths as dfSubMonths,
  subYears as dfSubYears,
  sub as dfSub,
  isWithinInterval as dfIsWithinInterval,
  getUnixTime,
} from 'date-fns';
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

import { useAuthContext } from '/src/hooks';
import { SPECIAL_DATE_RANGES, TIME_UNITS } from '/src/features/_global/constants';

const { TODAY, LAST_7_DAYS, LAST_30_DAYS, LAST_100_DAYS, LAST_MONTH, THIS_MONTH } = SPECIAL_DATE_RANGES;

const singleDay = 60 * 60 * 24;

export function useTimeZonedFunctions(forcedTimeZone) {
  const { currentUser } = useAuthContext();

  // dummy for now, maybe we'll do it per site instead. Point is we'll need to be in a hook anyhow.
  const timeZone = forcedTimeZone ?? currentUser?.settings?.timezone ?? 'UTC';

  if (!isValidTimeZone(timeZone)) throw new Error(`Invalid time zone: ${timeZone}`);

  const helpers = useMemo(() => {
    function format(date, format, options) {
      const parsedDate = toDate(date);
      return formatInTimeZone(parsedDate, timeZone, format, options);
    }

    function formatDay(date) {
      return format(date, 'MMM d, yyyy');
    }

    function formatDayFull(date) {
      return format(date, 'EEE, MMM d, yyyy');
    }

    function formatRangeSameMonth(start, end) {
      return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`;
    }

    function formatRangeSameYear(start, end) {
      return `${format(start, 'MMM d')} - ${formatDay(end)}`;
    }

    function formatRangeDifferentYear(start, end) {
      return `${formatDay(start)} - ${formatDay(end)}`;
    }

    function formatChartLabel(date, timeUnit = TIME_UNITS.DAY) {
      if (timeUnit === TIME_UNITS.MONTH) {
        return format(date, 'MMM yyyy');
      }
      return format(date, 'MMM d');
    }

    function formatChartTooltip(start, end, timeUnit = TIME_UNITS.DAY) {
      if (timeUnit === TIME_UNITS.DAY) {
        return format(start, 'EEE, MMM d');
      }
      if ([TIME_UNITS.WEEK, TIME_UNITS.MONTH].includes(timeUnit)) {
        if (isSameDay(start, end)) {
          return `${format(start, 'MMM d')}`;
        }
        if (isSameMonth(start, end)) {
          return `${format(start, 'MMM d')} - ${format(end, 'd')}`;
        }
        return `${format(start, 'MMM d')} - ${format(end, 'MMM d')}`;
      }
      throw new Error(`Invalid timeUnit: ${timeUnit}`);
    }

    function isSameDay(left, right) {
      return compareInTz(left, right, timeZone, dfIsSameDay);
    }

    function isSameMonth(left, right) {
      return compareInTz(left, right, timeZone, dfIsSameMonth);
    }

    function isSameYear(left, right) {
      return compareInTz(left, right, timeZone, dfIsSameYear);
    }

    function startOfDay(input) {
      return mapInTz(input, timeZone, (d) => dfStartOfDay(d));
    }

    function endOfDay(input) {
      return mapInTz(input, timeZone, (d) => dfEndOfDay(d));
    }

    function addDays(input, amount) {
      return mapInTz(input, timeZone, (d) => dfAddDays(d, amount));
    }

    function subDays(input, amount) {
      return mapInTz(input, timeZone, (d) => dfSubDays(d, amount));
    }

    function startOfMonth(input) {
      return mapInTz(input, timeZone, (d) => dfStartOfMonth(d));
    }

    function endOfMonth(input) {
      return mapInTz(input, timeZone, (d) => dfEndOfMonth(d));
    }

    function addMonths(input, amount) {
      return mapInTz(input, timeZone, (d) => dfAddMonths(d, amount));
    }

    function subMonths(input, amount) {
      return mapInTz(input, timeZone, (d) => dfSubMonths(d, amount));
    }

    function subYears(input, amount) {
      return mapInTz(input, timeZone, (d) => dfSubYears(d, amount));
    }

    function sub(input, duration) {
      return mapInTz(input, timeZone, (d) => dfSub(d, duration));
    }

    function isWithinInterval(input, interval) {
      const zDate = toZonedDate(input, timeZone);
      const zStart = toZonedDate(interval.start, timeZone);
      const zEnd = toZonedDate(interval.end, timeZone);

      return dfIsWithinInterval(zDate, { start: zStart, end: zEnd });
    }

    function getConvertedDateRange(daterange, relativeTo = new Date()) {
      let startDate, endDate;

      const now = new Date();
      switch (daterange.special) {
        case TODAY:
          startDate = startOfDay(now);
          endDate = endOfDay(now);
          break;
        case LAST_7_DAYS:
          startDate = startOfDay(subDays(relativeTo, 7));
          endDate = endOfDay(subDays(relativeTo, 1));
          break;
        case LAST_30_DAYS:
          startDate = startOfDay(subDays(relativeTo, 30));
          endDate = endOfDay(subDays(relativeTo, 1));
          break;
        case LAST_100_DAYS:
          startDate = startOfDay(subDays(relativeTo, 100));
          endDate = endOfDay(subDays(relativeTo, 1));
          break;
        case THIS_MONTH:
          startDate = startOfMonth(now);
          endDate = endOfDay(now);
          break;
        case LAST_MONTH:
          startDate = startOfMonth(subMonths(now, 1));
          endDate = endOfMonth(subMonths(now, 1));
          break;
        default:
          startDate = toDate(daterange.start_date);
          endDate = toDate(daterange.end_date);
          break;
      }
      startDate = getUnixTime(startDate);
      endDate = getUnixTime(endDate);

      let timeUnit = TIME_UNITS.MONTH;
      if (endDate - startDate < 30 * singleDay) {
        timeUnit = TIME_UNITS.DAY;
      } else if (endDate - startDate < 180 * singleDay) {
        timeUnit = TIME_UNITS.WEEK;
      }

      return {
        startDate,
        endDate,
        timeUnit,
      };
    }

    return {
      timeZone: getTimeZoneAbbreviations(timeZone),
      format,
      formatDay,
      formatDayFull,
      formatRangeSameMonth,
      formatRangeSameYear,
      formatRangeDifferentYear,
      formatChartLabel,
      formatChartTooltip,
      isSameDay,
      isSameMonth,
      isSameYear,
      startOfDay,
      endOfDay,
      addDays,
      subDays,
      startOfMonth,
      endOfMonth,
      addMonths,
      subMonths,
      subYears,
      sub,
      isWithinInterval,
      getConvertedDateRange,
    };
  }, [timeZone]);

  return helpers;
}

export function toDate(input) {
  // Already a Date instance: return as-is
  if (input instanceof Date) return input;

  // Numeric: decide seconds vs milliseconds
  if (typeof input === 'number' && Number.isFinite(input)) {
    // seconds if too small to be milliseconds
    const ms = Math.abs(input) < 1e11 ? input * 1000 : input;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) {
      throw new RangeError(`Invalid numeric date input: ${input}`);
    }
    return d;
  }

  // String or other: let Date parse it
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) {
    throw new RangeError(`Invalid date input: ${input}`);
  }
  return d;
}

const supportedTimeZones = (() => {
  if (typeof Intl.supportedValuesOf === 'function') {
    return new Set(Intl.supportedValuesOf('timeZone'));
  }
  return null;
})();

function isValidTimeZone(tz) {
  if (typeof tz !== 'string') return false;

  const zone = tz.trim();
  if (zone === 'UTC') return true;

  if (supportedTimeZones) {
    return supportedTimeZones.has(zone);
  }

  // fallback for older runtimes
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

function toZonedDate(input, timeZone) {
  const date = toDate(input);
  return utcToZonedTime(date, timeZone);
}

// Generic “compare two dates in a time zone”
function compareInTz(left, right, timeZone, comparator) {
  const l = toZonedDate(left, timeZone);
  const r = toZonedDate(right, timeZone);
  return comparator(l, r);
}

// Generic “transform one date in a time zone and return the UTC instant”
function mapInTz(input, timeZone, transformer) {
  const zoned = toZonedDate(input, timeZone);
  const localResult = transformer(zoned);
  return zonedTimeToUtc(localResult, timeZone);
}

function getDefaultLocale() {
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }

  try {
    const opts = Intl.DateTimeFormat().resolvedOptions();
    if (opts && opts.locale) return opts.locale;
  } catch {
    // noop
  }

  return 'en-US';
}

// Returns: { full: 'Europe/Paris', current: "CEST", primary: "CET" }
// Or for US zones: { full: 'America/New_York', current: "EDT", primary: "ET" }
// Or for zones with no abbreviations: { full: 'Asia/Calcutta', current: "GMT+5:30", primary: "GMT+5:30" }
export function getTimeZoneAbbreviations(timeZone, date = new Date()) {
  let current = null;
  const locale = getDefaultLocale();
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(date);

    const tz = parts.find((p) => p.type === 'timeZoneName');
    current = tz ? tz.value : null;
  } catch {
    return { full: timeZone, current: null, primary: null };
  }

  if (!current) {
    return { full: timeZone, current: null, primary: null };
  }

  // ---- MAKE PRIMARY ABBREVIATION ----

  // Case 1: Classic DST abbreviations (CET / CEST, EST / EDT, etc.)
  // If abbreviation ends with 'T' (-> "CET", "IST", "UTC") we treat it as primary.
  // If it ends with 'ST' or 'DT', drop the last letter ("EST"/"EDT" -> "ET").
  if (/^[A-Z]{2,4}$/.test(current)) {
    if (current.endsWith('DT')) {
      return { full: timeZone, current, primary: current.slice(0, -2) + 'T' }; // EDT -> ET
    }
    if (current.endsWith('ST')) {
      return { full: timeZone, current, primary: current.slice(0, -2) + 'T' }; // EST -> ET
    }
    if (current.endsWith('T')) {
      return { full: timeZone, current, primary: current }; // CET, JST, WET, UTC
    }
  }

  // Case 2: Offset zones like "GMT+5:30", "GMT+9"
  // These zones have no canonical abbreviation; primary = current
  return { full: timeZone, current, primary: current };
}
